import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import type {
  InboxConversationDetail,
  InboxConversationSummary,
  ReviewerDisplayState
} from './inboxTypes'
import { fetchInboxConversation } from './services/fetchInboxConversation'
import { ConversationList } from './ConversationList'
import { ConversationPanel } from './ConversationPanel'
import { InboxEmptyState } from './InboxEmptyState'
import { getDisplayStateForConversation } from './utils'

type DetailState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'success', conversation: InboxConversationDetail }

interface InboxPageViewProps {
  conversations: InboxConversationSummary[]
  reviewerDisplayState: ReviewerDisplayState
  onAfterSendMessage: (messageBody: string) => void | Promise<void>
  onConversationOpened?: (conversationId: number) => void | Promise<void>
}

export const InboxPageView: React.FC<InboxPageViewProps> = ({
  conversations,
  reviewerDisplayState,
  onAfterSendMessage,
  onConversationOpened
}) => {
  const navigate = useNavigate()
  const { conversationId } = useParams()

  const selectedId =
    conversationId != null && Number.isFinite(Number(conversationId))
      ? Number(conversationId)
      : null

  const [activeConversationId, setActiveConversationId] = useState<number | null>(selectedId)
  const [detailState, setDetailState] = useState<DetailState>({ status: 'idle' })
  const requestIdRef = useRef(0)
  const lastRequestedConversationIdRef = useRef<number | null>(null)

  const loadConversationById = useCallback(async (
    conversationIdToLoad: number | null,
    showLoadingState: boolean,
    refreshSummariesOnSuccess: boolean
  ): Promise<void> => {
    lastRequestedConversationIdRef.current = conversationIdToLoad
    const requestId = ++requestIdRef.current

    if (conversationIdToLoad == null) {
      setDetailState({ status: 'idle' })
      return
    }

    let loadedSuccessfully = false

    try {
      if (showLoadingState) {
        setDetailState({ status: 'loading' })
      }

      const detail = await fetchInboxConversation(conversationIdToLoad)
      if (requestId !== requestIdRef.current) return
      setDetailState({ status: 'success', conversation: detail })
      loadedSuccessfully = true
    } catch {
      if (requestId !== requestIdRef.current) return
      setDetailState({ status: 'error' })
    }

    if (loadedSuccessfully && refreshSummariesOnSuccess) {
      try {
        await onConversationOpened?.(conversationIdToLoad)
      } catch (error) {
        console.error('Failed to refresh inbox summaries after opening conversation', error)
      }
    }
  }, [onConversationOpened])

  useEffect(() => {
    setActiveConversationId(selectedId)
    if (selectedId === lastRequestedConversationIdRef.current) return
    void loadConversationById(selectedId, true, true)
  }, [selectedId, loadConversationById])

  const handleSendMessage = async (messageBody: string): Promise<undefined> => {
    try {
      await onAfterSendMessage(messageBody)
    } catch (error) {
      console.error('Failed to refresh inbox summaries after send', error)
    }
    await loadConversationById(activeConversationId, false, false)
    return undefined
  }

  const handleIssueStatusChange = async (): Promise<void> => {
    await loadConversationById(activeConversationId, false, true)
  }

  let conversationContent: React.ReactNode

  if (activeConversationId == null) {
    conversationContent = <InboxEmptyState />
  } else if (detailState.status === 'idle' || detailState.status === 'loading') {
    conversationContent = <div className='inbox-conversation-state'>Loading conversation…</div>
  } else if (detailState.status === 'error') {
    conversationContent = <div className='inbox-conversation-state'>Unable to load conversation.</div>
  } else {
    const displayStateForConversation = getDisplayStateForConversation(detailState.conversation)

    conversationContent = (
      <ConversationPanel
        conversation={detailState.status === 'success' ? detailState.conversation : null}
        reviewerDisplayState={displayStateForConversation}
        onIssueStatusChange={handleIssueStatusChange}
        onSendMessage={handleSendMessage}
      />
    )
  }

  return (
    <div className='inbox-view'>
      <div className='inbox-layout'>
        <div className='inbox-sidebar'>
          <div className='inbox-sidebar-header'>
            <h2 className='inbox-sidebar-title'>Inbox</h2>
          </div>

          <div className='message-cell-container'>
            <ConversationList
              conversations={conversations}
              selectedConversationId={activeConversationId}
              onSelect={(id) => {
                setActiveConversationId(id)
                setDetailState({ status: 'loading' })
                void navigate(`/${id}`)
                void loadConversationById(id, true, true)
              }}
            />
          </div>
        </div>

        <div className='inbox-conversation' id='conversation'>
          {conversationContent}
        </div>
      </div>
    </div>
  )
}
